// src/lib/walkthrough/resolveFallback.ts
//
// Pure (DOM-free at the type level) decision logic for the knowledge-base
// WalkthroughPlayer's element resolver. Extracted so the invariant below can be
// unit-guarded under `src/lib/**` (vitest `node` env) — see docs/REGRESSIONS.md.
//
// THE BUG (recurring; 1st 2026-06-30, 2nd 2026-06-30 same day): tutorial 6 step
// "Click "123 Main Street" suggestion" spotlighted the WRONG element — the
// dashboard's static "Delivery watch" address card ("123 Main Street,
// Windermere, FL 34786", Karen Anderson), which sits BEHIND the Create-New
// panel — instead of a row inside the address-autocomplete dropdown.
//
// 1st fix attempt (commit a09edcc): added `allowGlobalTextFallback()` + made the
// inline tour-id branch return null when the hook is absent. WHY IT DIDN'T HOLD:
//   (a) `allowGlobalTextFallback()` was never actually CALLED by `resolveElement`
//       — it guarded dead code; the only wired change was the inline null-return.
//   (b) The suggestion rows are dynamic (Google Places): only the FIRST row
//       carries the `[data-tour-id="create-contact-address-suggestion"]` hook,
//       and its text is frequently NOT "123 Main Street". So the resolver either
//       returned the wrong (but in-dropdown) first row, or — whenever the precise
//       hook wasn't matched — fell through to the page-wide quoted-text scan,
//       which grabbed the Delivery-watch card OUTSIDE the tour's scope.
//
// THE FIX (this change): resolution for the suggestion step is SCOPED STRICTLY
// to the dropdown container. We find the container (a stable
// `[data-tour-id="create-contact-address-suggestions"]` hook on the dropdown
// div, or the parent of the first-row hook as a fallback) and pick a row from
// INSIDE it — the row whose text matches the quoted address if present, else the
// FIRST suggestion row. If the dropdown isn't open we return null and the
// MutationObserver keeps retrying. There is NO page-wide fallback for this step,
// so it can NEVER select the Delivery-watch card or anything outside the panel.

// Matches a stable tour hook selector, e.g. `[data-tour-id="create-new-btn"]`
// or `[data-tour-id="create-contact-address-suggestion"]`.
export const TOUR_ID_SELECTOR_RE = /\[data-tour-id\s*[~|^$*]?=\s*["']?[^\]"']+["']?\]/i

export function isTourIdSelector(selector: string | null | undefined): boolean {
  return !!selector && TOUR_ID_SELECTOR_RE.test(selector)
}

/**
 * True when the resolver is allowed to fall back to a global quoted-text page
 * scan for this step. Returns false for steps anchored to a stable data-tour-id
 * hook — those must resolve via the hook (or not at all), never via a page-wide
 * text match that can grab an unrelated element sharing the same text.
 */
export function allowGlobalTextFallback(step: { selector?: string | null }): boolean {
  return !isTourIdSelector(step.selector)
}

// ── Address-suggestion step: strict dropdown scoping ────────────────────────

// The dropdown CONTAINER hook (added in AddressInput, src/App.tsx) and the
// first-row hook (kept for click-targeting / manual-advance delegation).
export const ADDRESS_SUGGESTIONS_CONTAINER_SEL = '[data-tour-id="create-contact-address-suggestions"]'
export const ADDRESS_SUGGESTION_ROW_SEL = '[data-tour-id="create-contact-address-suggestion"]'

// Every Create-New / Smart-Create autocomplete dropdown that a "...suggestion"
// tour step can target. A suggestion step is detected purely by its visible text
// (/suggestion/i) — it does NOT say WHICH field — so resolution must be able to
// scope to ANY of these create-panel dropdowns, not just the address one.
//
// 4th-OCCURRENCE FIX (2026-06-30): the "Create A Deal" tour's step
// `Click "TEST DEAL" suggestion` targets the CONTACT-NAME dropdown, which had no
// container hook. findSuggestionContainer only knew the ADDRESS container, so it
// returned null and the step could never locate the (correctly rendered) row —
// "Couldn't auto-locate this element." Adding the contact-name (and company)
// container hooks here + on the dropdowns in src/App.tsx makes the same strict,
// page-scan-free scoping work for the contact/company suggestion lists too.
export const CREATE_SUGGESTION_CONTAINER_SELECTORS: string[] = [
  ADDRESS_SUGGESTIONS_CONTAINER_SEL,
  '[data-tour-id="create-contact-name-suggestions"]',
  '[data-tour-id="create-company-name-suggestions"]',
  // Project-record address autocomplete (the "Assign a Project to an Owner" /
  // "Approving a Project for Owner" tours). The Test Project fixture starts with
  // NO address + NO start date, so the owner-select step is gated; the tour now
  // adds a "Type the address" + "Click the address suggestion" step before it.
  // That suggestion step (detected by /suggestion/i text) must scope strictly to
  // THIS dropdown — never a page-wide scan — exactly like the create-panel ones.
  '[data-tour-id="project-address-suggestions"]',
]

// Smart-quote-aware quoted-text extractor (mirrors QUOTED_RE in WalkthroughPlayer).
const QUOTED_RE = /[""""'']([^""""''\n]+)[""""'']/

/**
 * True for the tour step that targets the address-autocomplete suggestion list
 * (tutorial 6, "Click "123 Main Street" suggestion").
 *
 * 3rd-OCCURRENCE FIX: the REAL offending step has EMPTY selector AND EMPTY xpath
 * — it resolves purely via `target_text`. So detection must NOT key on
 * `selector` alone (that was the 2nd-round bug: `isAddressSuggestionStep(step.selector)`
 * never fired, and resolution fell through to the page-wide target_text scan that
 * grabbed the Delivery-watch card). The reliable signal is the visible step TEXT,
 * which always contains "suggestion". Any step whose text mentions a "suggestion"
 * pick is an autocomplete-dropdown selection and MUST be scoped to the dropdown,
 * never a page-wide text scan. The selector-substring check is kept as an extra
 * trigger for steps that still carry the per-row hook.
 */
export function isAddressSuggestionStep(
  step: { selector?: string | null; text?: string | null },
): boolean {
  if (step.selector && step.selector.includes('create-contact-address-suggestion')) return true
  if (step.text && /suggestion/i.test(step.text)) return true
  return false
}

/**
 * The text the suggestion resolver matches a dropdown row against: the step's
 * `target_text` if present, else the quoted phrase inside the step text.
 */
export function suggestionWantText(
  step: { text?: string | null; target_text?: string | null },
): string {
  const tt = (step.target_text || '').trim()
  if (tt) return tt
  const m = step.text ? step.text.match(QUOTED_RE) : null
  return m ? m[1].trim() : ''
}

// Minimal structural view of a DOM node the suggestion resolver reads. Real
// `document`/`HTMLElement` satisfy this (cast at the call site); the unit test
// supplies a hand-built fake so the guard runs in vitest's DOM-free `node` env.
export interface SuggestionDomNode {
  readonly textContent: string | null
  readonly children: ArrayLike<SuggestionDomNode>
  readonly parentElement: SuggestionDomNode | null
  getAttribute(name: string): string | null
}
export interface SuggestionDomRoot {
  querySelector(selector: string): SuggestionDomNode | null
}

const normText = (s: string | null | undefined): string =>
  (s || '').trim().toLowerCase().replace(/\s+/g, ' ')

/**
 * Locate the open address-suggestion dropdown container. Prefers the stable
 * container hook; falls back to the parent of the first-row hook (covers a
 * deploy where only the per-row hook is present). Returns null when the
 * dropdown is not open.
 */
export function findSuggestionContainer(root: SuggestionDomRoot): SuggestionDomNode | null {
  // Try every known create-panel suggestion container (address, contact-name,
  // company) — whichever is currently open. The step text doesn't say which
  // field, so we scope to the first open dropdown among them.
  for (const sel of CREATE_SUGGESTION_CONTAINER_SELECTORS) {
    const direct = root.querySelector(sel)
    if (direct) return direct
  }
  // Fallback: the address dropdown in a deploy that only carries the per-row hook.
  const hookRow = root.querySelector(ADDRESS_SUGGESTION_ROW_SEL)
  return hookRow ? hookRow.parentElement : null
}

/**
 * Resolve the spotlight target for the address-suggestion step. Searches ONLY
 * within the dropdown container — NEVER the wider page.
 *
 *  - dropdown open + a row matches `wantText`  → that row
 *  - dropdown open + no row matches            → the FIRST suggestion row
 *  - dropdown closed / container absent        → null (observer waits)
 *
 * The return value is, by construction, always a child row of the dropdown
 * container or null — so it can never be the dashboard Delivery-watch card or
 * any element outside the Create-New panel.
 */
export function resolveAddressSuggestion(
  root: SuggestionDomRoot,
  wantText: string,
): SuggestionDomNode | null {
  const container = findSuggestionContainer(root)
  if (!container) return null
  const rows: SuggestionDomNode[] = Array.from(container.children as ArrayLike<SuggestionDomNode>)
  if (rows.length === 0) return null
  const want = normText(wantText)
  if (want) {
    const matched = rows.find(r => normText(r.textContent).includes(want))
    if (matched) return matched
  }
  return rows[0]
}

/**
 * Single source of truth for resolving the address-suggestion step's spotlight
 * target. `resolveElement` (WalkthroughPlayer) and the regression test BOTH call
 * this so they can never drift.
 *
 *  - NOT a suggestion step                → null (caller continues normal logic)
 *  - suggestion step, dropdown open       → matching row, else FIRST row
 *  - suggestion step, dropdown closed     → null (observer waits; NO page scan)
 *
 * Crucially, for a suggestion step the result is ALWAYS an in-dropdown row or
 * null — the page-wide target_text/quoted-text scan is fully bypassed, so the
 * spotlight can never land on the dashboard Delivery-watch card behind the panel.
 */
export function resolveSuggestionForStep(
  step: { selector?: string | null; text?: string | null; target_text?: string | null },
  root: SuggestionDomRoot,
): SuggestionDomNode | null {
  if (!isAddressSuggestionStep(step)) return null
  return resolveAddressSuggestion(root, suggestionWantText(step))
}

// ── Project-record OWNER-select step: strict owner-control scoping ──────────
//
// THE BUG (2026-06-30, same class as the suggestion bug): the project-record
// walkthrough step `Select "Maxwell Ritchie" as the owner` (kbtut tutorial 10
// step 5 / tutorial 11 step 6) has selector=NULL and an xpath that targets a
// `<select>` inside the "Owner" .crp-field. But the owner control was migrated
// from a native `<select>` to a custom `<button>` + portal picker, so the xpath
// matches NOTHING. Resolution then fell through to the page-wide quoted-text
// scan for "Maxwell Ritchie", which grabbed the TOP-NAV user-menu button (the
// header literally reads "Maxwell Ritchie" for the logged-in user).
//
// THE FIX: detect the owner-select step by its visible text and resolve it
// strictly to the project-record owner control — the `project-owner-select`
// button, or (when the picker is open) the matching option row INSIDE
// `project-owner-options`. Never a page-wide scan, so it can never hit the
// top-nav user menu. When the owner control isn't on the page, resolution
// returns null so the caller continues its normal logic (this step only
// short-circuits on the project record).

export const PROJECT_OWNER_SELECT_SEL = '[data-tour-id="project-owner-select"]'
export const PROJECT_OWNER_OPTIONS_SEL = '[data-tour-id="project-owner-options"]'
// Per-row hooks added in src/App.tsx: every owner option row carries
// `project-owner-option`; the injected tutorial demo owner (Johnny Test) also
// carries the distinct `project-owner-option-demo`. These make the owner-select
// step resolve to the actual owner-name ROW regardless of the owner's current
// name (the demo owner was renamed Maxwell Ritchie → Johnny Test on 2026-07-01,
// which left the step's target_text stale and broke text-only matching).
export const PROJECT_OWNER_OPTION_DEMO_SEL = '[data-tour-id="project-owner-option-demo"]'
export const PROJECT_OWNER_OPTION_SEL = '[data-tour-id="project-owner-option"]'

/**
 * True for a "select … as the owner" walkthrough step. Keyed on the visible step
 * text (the step has NULL selector and a stale `<select>` xpath) — the same
 * text-first strategy that fixed the suggestion step.
 */
export function isOwnerSelectStep(step: { text?: string | null }): boolean {
  return !!step.text && /\bowner\b/i.test(step.text)
}

// Most specific descendant of `container` whose text contains `want` (smallest
// matching textContent → the leaf row/label, not the whole container). Scoped:
// only ever returns a node inside `container`.
function findScopedDescendantByText(
  container: SuggestionDomNode,
  want: string,
): SuggestionDomNode | null {
  const w = normText(want)
  if (!w) return null
  let best: SuggestionDomNode | null = null
  let bestLen = Infinity
  const walk = (n: SuggestionDomNode) => {
    const t = normText(n.textContent)
    if (t.includes(w) && t.length < bestLen) { best = n; bestLen = t.length }
    for (const c of Array.from(n.children as ArrayLike<SuggestionDomNode>)) walk(c)
  }
  for (const c of Array.from(container.children as ArrayLike<SuggestionDomNode>)) walk(c)
  return best
}

/**
 * Resolve the spotlight target for an owner-select step. When the picker is open
 * we resolve, in priority order, to the actual owner-name ROW:
 *   1. the row whose text matches `target_text` / the quoted owner name;
 *   2. the injected demo owner row (`project-owner-option-demo`) — stable even
 *      when the demo owner is renamed and `target_text` goes stale;
 *   3. the FIRST generic owner option row (`project-owner-option`);
 *   4. the owner-select control button (so clicking it opens the picker) — only
 *      when the picker is CLOSED (no option rows present).
 * When the owner control isn't on this page we return null so the caller
 * continues its normal resolution (this step does not apply here).
 *
 * The result is ALWAYS the owner button or a node inside/at the owner picker —
 * NEVER the top-nav user-menu button, the Owner-field wrapper, or the Assign
 * button. Falling back to the button (old behavior) when no row matched the
 * stale name is exactly the bug this fixes: after the demo owner rename the
 * spotlight left the owner ROW and landed on the wrong element.
 */
export function resolveOwnerSelectForStep(
  step: { text?: string | null; target_text?: string | null },
  root: SuggestionDomRoot,
): SuggestionDomNode | null {
  if (!isOwnerSelectStep(step)) return null
  const btn = root.querySelector(PROJECT_OWNER_SELECT_SEL)
  if (!btn) return null
  const options = root.querySelector(PROJECT_OWNER_OPTIONS_SEL)
  if (options) {
    // 1. Exact/contains text match on the wanted owner name (works today with a
    //    correct target_text; degrades gracefully if the name is stale).
    const byText = findScopedDescendantByText(options, suggestionWantText(step))
    if (byText) return byText
    // 2. The injected demo owner row — a stable hook that survives renames.
    const demoRow = root.querySelector(PROJECT_OWNER_OPTION_DEMO_SEL)
    if (demoRow) return demoRow
    // 3. The first generic owner option row — still a real owner ROW, never the
    //    button/wrapper. (querySelector returns the first match in document order.)
    const firstRow = root.querySelector(PROJECT_OWNER_OPTION_SEL)
    if (firstRow) return firstRow
  }
  return btn
}
